import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AppWindow, BarChart3, FileCode2, FileText, Presentation, Shapes, Table2, Workflow } from 'lucide-react';
import { api } from '../../lib/api';
import type { ArtifactSummary } from '../../types/api';
import { relativeTime } from '../../lib/format';

export const typeIcon = (t: string, size = 18) => ({
  html: <AppWindow size={size} />, react: <BarChart3 size={size} />, document: <FileText size={size} />, slides: <Presentation size={size} />,
  sheet: <Table2 size={size} />, svg: <Shapes size={size} />, mermaid: <Workflow size={size} />,
} as Record<string, React.ReactElement>)[t] ?? <FileCode2 size={size} />;

/** Inline card in chat. Opens the artifact in the side panel on the chat page. */
export function ArtifactCard({ artifactId, fallbackTitle }: { artifactId: string; fallbackTitle?: string }) {
  const [, setParams] = useSearchParams();
  const loc = useLocation();
  const nav = useNavigate();
  // Same cache entry as the panel, so live updates refresh the card too.
  const q = useQuery({ queryKey: ['artifact-summary', artifactId], queryFn: () => api<{ artifact: ArtifactSummary }>(`/api/artifacts/${artifactId}?files=false`).then((r) => r.artifact), retry: false });
  const a = q.data;
  const open = () => {
    if (loc.pathname.startsWith('/chat/')) setParams((p) => { const n = new URLSearchParams(p); n.set('artifact', artifactId); return n; }, { replace: true });
    else nav(`/artifacts/${artifactId}`);
  };
  if (q.isError) return <div className="artifact-card artifact-card--gone">{fallbackTitle ?? 'Artifact'} (deleted)</div>;
  return (
    <button className="artifact-card" onClick={open} data-testid="artifact-card">
      <span className="artifact-card__icon">{typeIcon(a?.type ?? '')}</span>
      <span className="artifact-card__text">
        <span className="artifact-card__title">{a?.title ?? fallbackTitle ?? 'Artifact'}</span>
        <span className="artifact-card__sub">{a ? `v${a.versionNumber} · ${relativeTime(a.updatedAt)}` : 'Loading…'}</span>
      </span>
      <span className="artifact-card__open">Open</span>
    </button>
  );
}
